import useRequest from "@/lib/request/useRequest";
import { addToast } from "@heroui/react";
import { useCallback, useMemo, useState } from "react";

function getDownloadFilename(contentDisposition: string | undefined) {
    const filename = contentDisposition?.match(/filename="?([^";]+)"?/)?.[1];
    return filename ?? "sentinel_boundary_shapefiles.zip";
}

function downloadBlob(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
}

export default function useBulkBoundaryDownload(fieldIds: number[]) {
    const { axios } = useRequest();
    const [isDownloading, setIsDownloading] = useState(false);

    const selectedFieldIds = useMemo(() => {
        return Array.from(new Set(fieldIds));
    }, [fieldIds]);

    const downloadBoundaries = useCallback(async () => {
        if (isDownloading || selectedFieldIds.length === 0) return;

        setIsDownloading(true);
        try {
            const response = await axios.put<Blob>(
                "fields/bulk_boundary_download/",
                {
                    field_ids: selectedFieldIds,
                },
                {
                    responseType: "blob",
                },
            );

            const filename = getDownloadFilename(
                response.headers["content-disposition"],
            );
            downloadBlob(
                new Blob([response.data], {
                    type: "application/zip",
                }),
                filename,
            );

            addToast({
                title: "Shapefiles downloaded",
                timeout: 3000,
                color: "success",
            });
        } catch {
            addToast({
                title: "Failed to download shapefiles",
                timeout: 3000,
                color: "danger",
            });
        } finally {
            setIsDownloading(false);
        }
    }, [axios, isDownloading, selectedFieldIds]);

    return {
        downloadBoundaries,
        isDownloading,
    };
}
